import { t } from '../i18n.ts';
import { isTouch } from '../settings.ts';
import { h } from '../ui/dom.ts';

/** The choice, kept on the device for every goal panel that folds (the cellars, the Furrows). */
const KEY = 'somnhold.goalFolded';
/** How long a folded panel opens to show a new order before it folds back. */
const PEEK_MS = 6000;

/** The stored choice; with none, phones start folded (the open panel covers half the room) and desktops open. */
const stored = (): boolean => {
  try {
    const v = localStorage.getItem(KEY);
    if (v !== null) return v === '1';
  } catch {
    /* fine */
  }
  return isTouch();
};

/**
 * A goal panel's corner button: folded, the panel keeps its title, its bar and the clocks that press, and the room
 * shows. While folded, a new order opens the panel for a few seconds and folds it back, so it isn't missed; a tap
 * anywhere on the folded panel opens it for good.
 */
export class GoalFold {
  readonly el: HTMLButtonElement;
  private said = '';
  private peekTimer = 0;
  /** The player's choice; the panel may be open for a moment while this says folded. */
  private want = false;

  constructor(
    private goal: HTMLElement,
    test: string,
  ) {
    this.el = h('button.ex-fold', {
      type: 'button',
      'data-test': test,
      onclick: (e: Event) => {
        e.stopPropagation();
        this.set(!this.folded, true);
      },
    }) as HTMLButtonElement;
    goal.addEventListener('click', () => {
      if (this.folded) this.set(false, true);
    });
    this.set(stored(), false);
  }

  get folded(): boolean {
    return this.goal.classList.contains('folded');
  }

  private set(on: boolean, keep: boolean): void {
    this.want = on;
    this.stopPeek();
    this.show(on);
    if (!keep) return;
    try {
      localStorage.setItem(KEY, on ? '1' : '0');
    } catch {
      /* fine */
    }
  }

  private show(folded: boolean): void {
    this.goal.classList.toggle('folded', folded);
    this.el.textContent = folded ? '+' : '–';
    const label = t(folded ? 'goal.unfold' : 'goal.fold');
    this.el.setAttribute('aria-label', label);
    this.el.title = label;
    this.el.setAttribute('aria-expanded', String(!folded));
  }

  private stopPeek(): void {
    if (this.peekTimer) clearTimeout(this.peekTimer);
    this.peekTimer = 0;
  }

  /** The order shown now: a new one (the first too) while folded opens the panel for a moment. */
  say(text: string): void {
    // Orders carry counts that tick (the seconds while down, the wheels turned): only new words are a new order.
    const order = text.replace(/\d+/g, '#');
    if (order === this.said) return;
    this.said = order;
    if (!this.want || !text) return;
    this.show(false);
    this.goal.classList.remove('blink');
    void this.goal.offsetWidth;
    this.goal.classList.add('blink');
    this.stopPeek();
    this.peekTimer = window.setTimeout(() => {
      this.peekTimer = 0;
      if (this.want) this.show(true);
    }, PEEK_MS);
  }

  /**
   * Keeps what shares the top of the screen with the panel (the party list) just under it on narrow screens, where
   * they would overlap: the CSS reads `--goal-bottom` there.
   */
  follow(below: HTMLElement): void {
    const place = (): void => below.style.setProperty('--goal-bottom', `${this.goal.offsetTop + this.goal.offsetHeight + 6}px`);
    new ResizeObserver(place).observe(this.goal);
    place();
  }
}
